import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import type { ReasonCode, ReplyTemplate } from '../api/useSettings.ts';
import type { Reply } from '../api/useSubmissions.ts';
import { Ikon } from '../status/Ikon.tsx';
import { AVVISNINGSKODER, SVAR_MAKS, avvisningsnavn } from '../status/status.ts';

export interface SvarTilstand {
  tekst: string;
  setTekst: (t: string) => void;
  /** Malen teksten startet fra — serveren sammenligner og nekter en uendret mal ved avvist (DD-09). */
  mal: ReplyTemplate | null;
  setMal: (m: ReplyTemplate | null) => void;
  kode: ReasonCode | '';
  setKode: (k: ReasonCode | '') => void;
  svarRef: RefObject<HTMLTextAreaElement | null>;
}

/**
 * Plassen i en mal der begrunnelsen mangler. Leietakerens mal skriver den som «…»
 * («Vi prioriterer det ikke nå, fordi …»); panelet tegner den som en stiplet plass med
 * ordene «skriv hvorfor», så det SYNES hva som mangler før avslaget sendes (DD-09).
 * Teksten i feltet er fortsatt malen ordrett — låsen på uendret mal sammenligner med den,
 * og serveren gjør det samme (422 template_unmodified). En mal uten «…» fylles inn som den er.
 */
export const MAL_PLASS = '…';

/** Svaret som alt er sendt (DD-27): samme tekst som innsenderen ser. Ett svar, aldri tråd. */
export function Svarkort({ svar }: { svar: Reply }) {
  return (
    <div role="note" aria-label="Svar sendt til innsender" className="panel-svarkort" data-testid="svarkort">
      <p className="panel-svarkort-hode">
        <Ikon navn="mail-outline" /> Sendt til innsender {new Date(svar.at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })}
        {svar.reasonCode ? ` · ${avvisningsnavn(svar.reasonCode)}` : ''}
      </p>
      <p className="panel-svarkort-tekst">«{svar.text}»</p>
      <p className="panel-etikett">Samme tekst som innsenderen ser.</p>
    </div>
  );
}

/**
 * Svaret til innsenderen (DD-66, DD-27): ETT felt, åpent, `r` flytter markøren hit. Ett svar
 * per sak, aldri tråd.
 *
 * Er Avvist valgt (DD-09, presisert K1): avslagskodene står som ETT radiovalg over feltet —
 * ingen forhåndsvalgt — og koden fyller leietakerens mal rett i feltet, med den stiplede
 * plassen der begrunnelsen mangler. Forhåndsvisningen er ÉN utfoldbar linje, ikke en boks
 * som gjentar feltet: svaret vises ordrett to steder fra før (DD-27).
 *
 * Tegntelleren sier fra ved 80 og 100 prosent (ux-flow-architect), ikke for hvert tegn.
 */
export function Svar({ s, avvist, maler, kanSkrive }: { s: SvarTilstand; avvist: boolean; maler: ReplyTemplate[]; kanSkrive: boolean }) {
  const lengde = s.tekst.length;
  const andel = lengde / SVAR_MAKS;
  const teller = andel >= 1 ? `${lengde} av ${SVAR_MAKS} tegn — fullt` : andel >= 0.8 ? `${lengde} av ${SVAR_MAKS} tegn` : null;
  const malUendret = s.mal !== null && s.tekst.trim() === s.mal.text.trim();
  /** Malen står urørt i feltet OG har en plass igjen: da tegnes den, i stedet for å redigeres. */
  const [redigerer, setRedigerer] = useState(false);
  const visMal = avvist && malUendret && s.tekst.includes(MAL_PLASS) && !redigerer;
  const kodeRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Avvist uten kode: fokus går til kodene. Rekkefølgen er S → kode → felt → Ctrl+Enter, og
  // hvert steg peker på det neste — ingen leting etter hva som mangler.
  useEffect(() => {
    if (avvist && !s.kode) kodeRefs.current[0]?.focus();
  }, [avvist, s.kode]);

  // Feltet tas i bruk: markøren står i plassen, og den er MARKERT — første tegn skriver den
  // over, slik nettleseren selv gjør med en markert tekst. Ingen tastetrykk går tapt: malen
  // blir til feltet i det den får fokus, ikke i det noen begynner å skrive.
  useEffect(() => {
    if (!redigerer) return;
    const el = s.svarRef.current;
    if (!el) return;
    el.focus();
    const i = el.value.indexOf(MAL_PLASS);
    if (i < 0) el.setSelectionRange(el.value.length, el.value.length);
    else el.setSelectionRange(i, i + MAL_PLASS.length);
    // s er en tilstandsbærer som byttes hver render; effekten skal kjøre på `redigerer`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redigerer]);

  // Ny sak eller ny kode: malen tegnes igjen, ikke feltet den ble redigert i.
  useEffect(() => setRedigerer(false), [s.kode, s.mal?.id]);

  /** Malene som ikke hører til en avslagskode — de eneste som kan settes inn for hånd. */
  const friMaler = maler.filter((m) => !m.reasonCode);
  const [malMeny, setMalMeny] = useState(false);

  const velgMal = (m: ReplyTemplate) => {
    s.setTekst(m.text);
    s.setMal(m);
    setMalMeny(false);
    setTimeout(() => s.svarRef.current?.focus(), 0);
  };

  const velgKode = (k: ReasonCode) => {
    s.setKode(k);
    const mal = maler.find((m) => m.reasonCode === k);
    // Malen fylles inn når feltet er tomt eller står på en annen mal — aldri over noe skrevet.
    if (mal && (!s.tekst.trim() || malUendret)) {
      s.setTekst(mal.text);
      s.setMal(mal);
    }
  };

  const kodeTaster = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = Math.max(0, AVVISNINGSKODER.findIndex(([k]) => k === s.kode));
    const neste = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? Math.min(AVVISNINGSKODER.length - 1, i + 1) : Math.max(0, i - 1);
    velgKode(AVVISNINGSKODER[neste]![0]);
    kodeRefs.current[neste]?.focus();
  };

  const [foer, etter] = visMal ? [s.tekst.slice(0, s.tekst.indexOf(MAL_PLASS)), s.tekst.slice(s.tekst.indexOf(MAL_PLASS) + MAL_PLASS.length)] : ['', ''];

  return (
    <div className="panel-svar" data-testid="svar">
      {avvist ? (
        <div className="panel-koder">
          <p className="panel-svar-etikett" id="kode-h">
            Hvorfor avvises den?
          </p>
          <div role="radiogroup" aria-labelledby="kode-h" className="panel-koder-valg" onKeyDown={kodeTaster}>
            {AVVISNINGSKODER.map(([k, navn], i) => {
              const på = s.kode === k;
              return (
                <button
                  key={k}
                  ref={(el) => {
                    kodeRefs.current[i] = el;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={på}
                  tabIndex={på || (!s.kode && i === 0) ? 0 : -1}
                  disabled={!kanSkrive}
                  className={`panel-kode${på ? ' panel-kode-valgt' : ''}`}
                  onClick={() => velgKode(k)}
                  data-testid={`kode-${k}`}
                >
                  <Ikon navn={på ? 'radio-button-on' : 'radio-button-off'} />
                  {navn}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="panel-svar-hode">
        <label className="panel-svar-etikett" htmlFor="svartekst" id="svar-h">
          <Ikon navn="mail-outline" /> Svar til innsender{avvist ? ' · begrunnelsen' : ''}
        </label>
        <span className="panel-etikett">{avvist && s.kode ? `malen for «${avvisningsnavn(s.kode)}»` : <kbd>R</kbd>}</span>
      </div>

      {visMal ? (
        // Malen tegnet med plassen som mangler. Et trykk — eller en tast — gjør den til
        // feltet den er, med markøren i plassen.
        <div
          role="textbox"
          tabIndex={0}
          aria-multiline="true"
          aria-labelledby="svar-h"
          className="panel-svar-mal"
          data-testid="svar-mal"
          onFocus={() => setRedigerer(true)}
          onClick={() => setRedigerer(true)}
        >
          {foer}
          <span className="panel-svar-plass">skriv hvorfor</span>
          {etter}
        </div>
      ) : (
        <textarea
          id="svartekst"
          ref={s.svarRef}
          value={s.tekst}
          onChange={(e) => {
            s.setTekst(e.target.value);
          }}
          maxLength={SVAR_MAKS}
          rows={3}
          placeholder="Skriv ett svar."
          aria-keyshortcuts="r"
          disabled={!kanSkrive}
          aria-required={avvist || undefined}
          aria-invalid={avvist && !s.tekst.trim() ? true : undefined}
          data-testid="svartekst"
        />
      )}

      <div className="panel-svar-fot">
        {avvist && s.tekst.trim() ? (
          <details className="panel-forhaand" data-testid="forhaandsvisning">
            <summary>
              <Ikon navn="chevron-forward-outline" /> Slik ser innsenderen det
            </summary>
            <p className="panel-forhaand-tekst">«{s.tekst}»</p>
          </details>
        ) : !avvist && friMaler.length ? (
          // Malene UTEN avslagskode («Trenger mer») hører til det vanlige svaret: i
          // Avvist-flyten er det koden som fyller malen (DD-09), og da står ikke lenken.
          // Én tekstlenke, ingen brikkerad — brikkene var seks rammer på flaten (F4).
          <details className="panel-forhaand panel-mal-meny" open={malMeny} onToggle={(e) => setMalMeny(e.currentTarget.open)} data-testid="mal-meny">
            <summary>
              <Ikon navn="chevron-forward-outline" /> Sett inn mal
            </summary>
            <ul className="panel-mal-liste" aria-label="Maler">
              {friMaler.map((m) => (
                <li key={m.id}>
                  <button type="button" className="panel-knapp-lenke" disabled={!kanSkrive} title={m.text} onClick={() => velgMal(m)} data-testid={`mal-${m.id}`}>
                    {m.name}
                  </button>
                </li>
              ))}
            </ul>
          </details>
        ) : (
          <span />
        )}
        <span role="status" aria-live="polite" className="panel-etikett" data-testid="tegnteller">
          {teller}
        </span>
      </div>

      {malUendret ? (
        <p className="panel-grunn" role="status">
          <Ikon navn="close-circle-outline" /> Gjør malen til ditt eget svar før du sender.
        </p>
      ) : null}
    </div>
  );
}
